import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Lightbulb, User, ThumbsUp } from "lucide-react";
import { toast } from "sonner";
import { BACKEND_URL, API } from "@/lib/api";


export default function EventTips({ eventId, isOwner }) {
  const [tips, setTips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    author_name: "",
    tip_text: "",
    category: "general"
  });

  useEffect(() => {
    fetchTips();
  }, [eventId]);

  const fetchTips = async () => {
    try {
      const response = await axios.get(`${API}/events/${eventId}/tips`);
      setTips(response.data);
    } catch (error) {
      console.error("Failed to fetch tips:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.author_name || !formData.tip_text) {
      toast.error("Name und Tipp sind erforderlich!");
      return;
    }

    try {
      await axios.post(
        `${API}/events/${eventId}/tips`,
        formData
      );
      
      toast.success("Tipp hinzugefügt!");
      setFormData({ author_name: "", tip_text: "", category: "general" });
      fetchTips();
    } catch (error) {
      console.error("Failed to submit tip:", error);
      toast.error("Fehler beim Hinzufügen");
    }
  };

  const handleLike = async (tipId) => {
    try {
      await axios.post(`${API}/events/${eventId}/tips/${tipId}/like`);
      fetchTips();
    } catch (error) {
      console.error("Failed to like tip:", error);
    }
  };

  if (loading) {
    return (
      <Card className="p-6">
        <div className="flex items-center justify-center">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
        </div>
      </Card>
    );
  }

  const categories = [
    { value: "general", label: "Allgemein" },
    { value: "location", label: "Ort" },
    { value: "food", label: "Essen" },
    { value: "entertainment", label: "Unterhaltung" },
    { value: "accommodation", label: "Unterkunft" }
  ];

  return (
    <Card className="p-6">
      <div className="mb-6">
        <h3 className="text-lg font-semibold flex items-center gap-2 mb-2">
          <Lightbulb className="w-5 h-5 text-primary" />
          Tipps & Empfehlungen
        </h3>
        <p className="text-sm text-muted-foreground">
          {isOwner 
            ? "Sammle wertvolle Tipps von deinen Gästen"
            : "Teile deine besten Tipps und Empfehlungen"
          }
        </p>
      </div>

      {/* Add Tip Form */}
      {!isOwner && (
        <form onSubmit={handleSubmit} className="mb-6 p-4 border rounded-lg space-y-4 bg-secondary/20">
          <div>
            <Label htmlFor="author-name">Dein Name *</Label>
            <Input
              id="author-name"
              value={formData.author_name}
              onChange={(e) => setFormData({ ...formData, author_name: e.target.value })}
              placeholder="z.B. Max Mustermann"
              required
            />
          </div>

          <div>
            <Label htmlFor="category">Kategorie</Label>
            <select
              id="category"
              value={formData.category}
              onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              className="w-full px-3 py-2 border rounded-md bg-background"
            >
              {categories.map(cat => (
                <option key={cat.value} value={cat.value}>{cat.label}</option>
              ))}
            </select>
          </div>

          <div>
            <Label htmlFor="tip-text">Dein Tipp *</Label>
            <Textarea
              id="tip-text"
              value={formData.tip_text}
              onChange={(e) => setFormData({ ...formData, tip_text: e.target.value })}
              placeholder="Teile deinen Tipp oder Empfehlung..."
              rows={3}
              required
            />
          </div>

          <Button type="submit" className="w-full">
            Tipp hinzufügen
          </Button>
        </form>
      )}

      {/* Tips List */}
      <div className="space-y-4">
        {tips.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <Lightbulb className="w-12 h-12 mx-auto mb-2 opacity-30" />
            <p>Noch keine Tipps</p>
            {!isOwner && (
              <p className="text-xs mt-1">Sei der Erste und teile einen Tipp!</p>
            )}
          </div>
        ) : (
          tips.map((tip) => (
            <div key={tip.id} className="p-4 bg-secondary/20 rounded-lg">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <User className="w-4 h-4 text-muted-foreground" />
                    <span className="font-medium text-sm">{tip.author_name}</span>
                    <span className="px-2 py-0.5 bg-primary/10 rounded-full text-xs">
                      {categories.find(c => c.value === tip.category)?.label || tip.category}
                    </span>
                  </div>
                  <p className="text-sm text-foreground">{tip.tip_text}</p>
                </div>
                <button
                  onClick={() => handleLike(tip.id)}
                  className="flex items-center gap-1 px-2 py-1 rounded hover:bg-primary/10 transition-colors"
                >
                  <ThumbsUp className="w-4 h-4" />
                  <span className="text-xs">{tip.likes || 0}</span>
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </Card>
  );
}